import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { Coffee, Gift, Instagram, Share, Plus, Download, ArrowRight, Apple, Smartphone, AlertTriangle, MoreVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { InstallAppButton } from "@/components/InstallAppButton";
import { BAR_NAME, INSTAGRAM_URL, INSTAGRAM_HANDLE, IMAGES } from "@/lib/config";
import { STAMPS_PER_REWARD } from "@/lib/api";

const isInAppBrowser = () =>
  typeof window !== "undefined" &&
  /instagram|fbav|fban|fb_iab|line|whatsapp|tiktok|musical_ly/i.test(window.navigator.userAgent || "");

const Step = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-3 text-sm text-muted-foreground">
    <span className="flex items-center justify-center w-9 h-9 rounded-full bg-primary/15 ring-1 ring-primary/30 shrink-0">
      <Icon className="w-4 h-4 text-primary" />
    </span>
    {children}
  </div>
);

export default function InstallPage() {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const cardUrl = `${origin}/tessera`;

  return (
    <div className="relative z-[2] min-h-[calc(100vh-68px)] overflow-hidden">
      <img src={IMAGES.hero} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
      <div className="absolute inset-0 bg-background/85" />

      <div className="relative z-10 max-w-3xl mx-auto px-5 sm:px-6 py-14 sm:py-20">
        {isInAppBrowser() && (
          <motion.div
            initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
            data-testid="inapp-banner"
            className="mb-8 rounded-2xl bg-amber-500/10 ring-1 ring-amber-500/40 p-5 flex gap-3"
          >
            <AlertTriangle className="w-6 h-6 text-amber-400 shrink-0" />
            <div className="text-sm text-amber-100">
              <p className="font-semibold mb-1">Per installare l'app, aprila nel browser del telefono</p>
              <p className="text-amber-200/90 flex items-center gap-1 flex-wrap">
                Tocca il menu <MoreVertical className="w-4 h-4 inline" /> in alto a destra → <span className="text-white">"Apri nel browser"</span> (Safari o Chrome), poi premi "Installa l'app".
              </p>
            </div>
          </motion.div>
        )}

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center mb-10">
          <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/15 ring-1 ring-primary/30 mx-auto mb-5">
            <Coffee className="w-7 h-7 text-primary" />
          </div>
          <p className="font-mono text-xs tracking-[0.35em] text-primary mb-3">TESSERA FEDELTÀ DIGITALE</p>
          <h1 className="font-serif text-5xl sm:text-6xl leading-[0.95] mb-4">Installa l'app {BAR_NAME}</h1>
          <p className="text-lg text-muted-foreground max-w-xl mx-auto">
            Ogni {STAMPS_PER_REWARD} caffè, uno è offerto da noi. Salva la tua tessera sul telefono e non perdere più un timbro.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 gap-6">
          {/* QR */}
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            data-testid="install-qr-card"
            className="rounded-2xl bg-black/50 backdrop-blur-xl ring-1 ring-white/10 p-6 flex flex-col items-center justify-center gap-4">
            <div className="bg-white p-4 rounded-2xl">
              <QRCodeCanvas value={cardUrl} size={190} level="M" fgColor="#0a0a0a" bgColor="#ffffff" marginSize={2} data-testid="install-page-qr" />
            </div>
            <p className="text-sm text-muted-foreground text-center">Inquadra con la fotocamera del telefono per aprire la tua tessera.</p>
          </motion.div>

          {/* Steps + actions */}
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="rounded-2xl bg-black/50 backdrop-blur-xl ring-1 ring-white/10 p-6 flex flex-col gap-5">
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-1">
                <Apple className="w-4 h-4 text-primary" />
                <p className="font-mono text-xs tracking-widest text-primary">SU IPHONE (SAFARI)</p>
              </div>
              <Step icon={Share}>Tocca <span className="text-white">Condividi</span></Step>
              <Step icon={Plus}>Scegli <span className="text-white">"Aggiungi a Home"</span></Step>
            </div>
            <div className="space-y-4 pt-2 border-t border-white/5">
              <div className="flex items-center gap-2 mb-1 mt-1">
                <Smartphone className="w-4 h-4 text-primary" />
                <p className="font-mono text-xs tracking-widest text-primary">SU ANDROID (CHROME)</p>
              </div>
              <Step icon={Download}>Menu <span className="text-white">⋮</span> → <span className="text-white">"Installa app"</span></Step>
            </div>
            <div className="mt-auto pt-2">
              <InstallAppButton className="w-full justify-center" />
            </div>
          </motion.div>
        </div>

        {/* CTA row */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.35 }}
          className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link to="/tessera" className="w-full sm:w-auto">
            <Button data-testid="install-open-card" className="w-full rounded-full h-12 px-8 bg-primary hover:bg-amber-500 text-primary-foreground font-semibold transition-colors duration-300">
              <Gift className="w-4 h-4 mr-2" /> Apri la mia tessera <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
          <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" data-testid="install-instagram" className="w-full sm:w-auto">
            <Button variant="outline" className="w-full rounded-full h-12 px-8 border-white/20 bg-white/5 hover:bg-white/10 text-white font-semibold">
              <Instagram className="w-4 h-4 mr-2" /> @{INSTAGRAM_HANDLE}
            </Button>
          </a>
        </motion.div>

        <p className="text-center text-xs text-muted-foreground/60 mt-10">
          {BAR_NAME} · Caffè & Cocktail Bar · Napoli
        </p>
      </div>
    </div>
  );
}
